// Positionszeilen für alle Belege — Angebot, Auftrag, Lieferschein, Rechnung.
// Eine Zeile besteht aus Artikelauswahl (oder freiem Text), Menge, Einheit,
// Einzelpreis und Steuersatz. Preise lassen sich ausblenden, wenn der Beleg
// keine trägt.
//
// Die Einheit ist frei wählbar: Wird ein Artikel in Kilogramm geführt, darf
// die Position in Gramm stehen — das Backend rechnet für den Bestand um. Unter
// der Zeile steht, wie viel tatsächlich vom Lager abgeht. Passt die Einheit gar
// nicht (Stück gegen Kilogramm), steht dort die Warnung, bevor gespeichert wird.
import type { Artikel } from '../../../gemeinsam/lib/typen';
import { profil, menge as mengeText } from '../../../gemeinsam/lib/daten';
import { Auswahl, Feld, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import EinheitWahl from '../../artikel/komponenten/EinheitWahl';
import { einheitAnzeige, umrechnen } from '../../artikel/lib/einheiten';

export type Entwurfsposition = {
  produktId: string;
  bezeichnung: string;
  menge: string;
  einheit: string;
  einzelpreis: string;
  steuersatz: string;
};

/**
 * Eine leere Zeile. Der Steuersatz kommt aus dem Länderprofil des Betriebs —
 * in Deutschland 19 %, in China 13 %, bei freier Einstellung der dort
 * hinterlegte Standard.
 */
export function leerePosition(): Entwurfsposition {
  return {
    produktId: '', bezeichnung: '', menge: '1', einheit: 'Stück', einzelpreis: '0',
    steuersatz: String(profil().standardSteuersatz),
  };
}

const komma = (s: string) => Number(String(s).replace(',', '.')) || 0;

/** Rechnet den Entwurf durch — dieselbe Regel wie im Backend, nur für die Anzeige. */
export function summeAusEntwurf(positionen: Entwurfsposition[], rabattProzent = 0) {
  const faktor = 1 - rabattProzent / 100;
  let netto = 0;
  let steuer = 0;
  for (const p of positionen) {
    if (!p.bezeichnung.trim()) continue;
    const zeile = Math.round(komma(p.menge) * komma(p.einzelpreis) * 100) / 100 * faktor;
    netto += zeile;
    steuer += zeile * (Number(p.steuersatz) || 0) / 100;
  }
  netto = Math.round(netto * 100) / 100;
  steuer = Math.round(steuer * 100) / 100;
  return { netto, steuer, brutto: Math.round((netto + steuer) * 100) / 100 };
}

/** Wandelt den Entwurf in das Format, das die API erwartet. */
export function positionenFuerApi(positionen: Entwurfsposition[]) {
  return positionen
    .filter(p => p.bezeichnung.trim())
    .map(p => ({
      produktId: p.produktId || null,
      bezeichnung: p.bezeichnung,
      menge: komma(p.menge),
      einheit: p.einheit || 'Stück',
      einzelpreis: komma(p.einzelpreis),
      steuersatz: Number(p.steuersatz) || 0,
    }));
}

export function PositionenEditor({
  positionen, setzen, artikel, mitPreisen = true,
}: {
  positionen: Entwurfsposition[];
  setzen: (p: Entwurfsposition[]) => void;
  artikel: Artikel[];
  mitPreisen?: boolean;
}) {
  const { t } = useSprache();
  const aendern = (i: number, aenderung: Partial<Entwurfsposition>) =>
    setzen(positionen.map((p, k) => k === i ? { ...p, ...aenderung } : p));

  const artikelWaehlen = (i: number, produktId: string) => {
    const a = artikel.find(x => x.id === produktId);
    aendern(i, a
      ? {
        produktId, bezeichnung: a.name, einheit: a.einheit || 'Stück',
        einzelpreis: String(a.verkaufspreis).replace('.', ','), steuersatz: String(a.steuersatz),
      }
      : { produktId: '' });
  };

  /**
   * Einheit wechseln. Ist die neue Einheit umrechenbar, wandert der Preis
   * mit: 12 €/kg werden zu 0,012 €/g. Sonst stünde nach dem Umstellen auf
   * Gramm der Kilopreis je Gramm in der Zeile — das Tausendfache.
   *
   * Umgerechnet wird vom Preis, der gerade in der Zeile steht, nicht vom
   * Artikelpreis: ein eingetippter Sonderpreis bleibt so erhalten.
   */
  const einheitWechseln = (i: number, neu: string) => {
    const p = positionen[i];
    const faktor = neu ? umrechnen(1, neu, p.einheit || 'Stück') : null;
    if (faktor !== null && faktor > 0 && faktor !== 1) {
      const neuerPreis = Math.round(komma(p.einzelpreis) * faktor * 10000) / 10000;
      aendern(i, { einheit: neu, einzelpreis: String(neuerPreis).replace('.', ',') });
    } else {
      aendern(i, { einheit: neu });
    }
  };

  const spalten = mitPreisen
    ? 'sm:grid-cols-[minmax(0,1fr)_5rem_8.5rem_7rem_5.5rem_auto]'
    : 'sm:grid-cols-[minmax(0,1fr)_6rem_8.5rem_auto]';

  return (
    <div className="space-y-3">
      {positionen.map((p, i) => {
        const a = p.produktId ? artikel.find(x => x.id === p.produktId) : null;
        const lagerEinheit = a?.einheit || 'Stück';
        const lagermenge = a ? umrechnen(komma(p.menge), p.einheit || 'Stück', lagerEinheit) : null;
        const andereEinheit = a && (p.einheit || 'Stück') !== lagerEinheit;

        return (
          <div key={i}>
            <div className={`grid gap-2 ${spalten}`}>
              <div>
                <Auswahl label={i === 0 ? t('pruef.artikel') : ''} value={p.produktId}
                         onChange={e => artikelWaehlen(i, e.target.value)}>
                  <option value="">{t('positionen.freiText')}</option>
                  {artikel.map(x => (
                    <option key={x.id} value={x.id}>{x.name} ({mengeText(x.bestand, einheitAnzeige(x.einheit, t))})</option>
                  ))}
                </Auswahl>
                {!p.produktId && (
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    placeholder={t('feld.bezeichnung')} value={p.bezeichnung}
                    onChange={e => aendern(i, { bezeichnung: e.target.value })} />
                )}
              </div>
              <Feld label={i === 0 ? t('feld.menge') : ''} value={p.menge} inputMode="decimal"
                    onChange={e => aendern(i, { menge: e.target.value })} />
              <EinheitWahl wert={p.einheit} beiAenderung={e => einheitWechseln(i, e)}
                           label={i === 0 ? t('feld.einheit') : ''} />
              {mitPreisen && (
                <>
                  <Feld label={i === 0 ? t('feld.einzelpreis') : ''} value={p.einzelpreis} inputMode="decimal"
                        hinweis={p.einheit && p.einheit !== 'Stück'
                          ? t('positionen.jeEinheit').replace('{einheit}', einheitAnzeige(p.einheit, t)) : undefined}
                        onChange={e => aendern(i, { einzelpreis: e.target.value })} />
                  <Auswahl label={i === 0 ? profil().steuerKurz : ''} value={p.steuersatz}
                           onChange={e => aendern(i, { steuersatz: e.target.value })}>
                    {/* Die angebotenen Sätze stammen aus dem Länderprofil. Steht in
                        einer alten Position ein Satz, den es dort nicht mehr gibt,
                        bleibt er trotzdem wählbar — sonst würde er stillschweigend
                        auf einen anderen Wert springen. */}
                    {[...new Set([...profil().steuersaetze, Number(p.steuersatz)])]
                      .filter(n => Number.isFinite(n))
                      .sort((a, b) => b - a)
                      .map(s => <option key={s} value={String(s)}>{s} %</option>)}
                  </Auswahl>
                </>
              )}
              <div className={i === 0 ? 'sm:pt-[1.625rem]' : ''}>
                <Knopf variante="still" onClick={() => setzen(positionen.filter((_, k) => k !== i))}
                       disabled={positionen.length === 1}>
                  {t('allg.entfernen')}
                </Knopf>
              </div>
            </div>

            {andereEinheit && (
              lagermenge === null ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                  {t('positionen.passtNicht').replace('{einheit}', einheitAnzeige(lagerEinheit, t))}
                </p>
              ) : (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t('positionen.umgerechnet').replace('{menge}', mengeText(lagermenge, einheitAnzeige(lagerEinheit, t)))}
                </p>
              )
            )}
          </div>
        );
      })}
      <Knopf onClick={() => setzen([...positionen, leerePosition()])}>{t('positionen.hinzufuegen')}</Knopf>
    </div>
  );
}
